import React, { useEffect, useState } from 'react'
import { Box, Typography, Chip, Paper, Button, Rating, Modal } from '@mui/material'
import { ExternAppointment } from 'modules/calendar/models/ExternAppointment'
import {
  ExternAppointmentStatus,
  externAppointmentStatusTypes,
} from 'modules/calendar/enums/ExternAppointmentStatus'
import styles from './AppointmentDetail.module.css'
import genericStyle from 'common/utils/generic.module.css'
import { t } from 'i18next'
import { getCalendarContainer } from 'container/calendar-module'
import { EXTERN_APPOINTMENT_API_KEY } from 'modules/calendar'
import { IExternAppointmentService } from 'modules/calendar/services/ExternAppointmentService'
import dayjs from 'dayjs'
import { ExternAppointmentAction } from 'modules/calendar/enums/ExternAppointmentAction'
import { enqueueSnackbar } from 'notistack'
import { RejectAppointmentModal } from './RejectAppointmentModal'
import { AcceptCompleteAppointmentModal } from './AcceptCompleteAppointmentModal'

interface Props {
  appointmentId: string
}

const calendarContainer = getCalendarContainer()
const externAppointmentService = calendarContainer.get<IExternAppointmentService>(
  EXTERN_APPOINTMENT_API_KEY
)

export function AppointmentDetail({ appointmentId }: Props) {
  const getChipClass = (status: ExternAppointmentStatus): string => {
    const className = `chip${ExternAppointmentStatus[status]}`
    return styles[className] || ''
  }
  const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false)
  const [openAcceptCompleteModal, setOpenAcceptCompleteModal] = useState<boolean>(false)
  const [appointment, setAppointment] = useState<ExternAppointment>()
  const isPending = appointment?.status === ExternAppointmentStatus.Pending
  const isAccepted = appointment?.status === ExternAppointmentStatus.Accepted
  const isFinalized = appointment?.status === ExternAppointmentStatus.Finalized
  const handleClose = () => setRejectModalOpen(false)

  useEffect(() => {
    externAppointmentService.getByID(appointmentId).subscribe((res) => {
      if (!res) {
        return
      }
      setAppointment(res)
    })
  }, [])

  const getSuccessText = (action: ExternAppointmentAction) => {
    switch (action) {
      case ExternAppointmentAction.Accept:
        return t('appointmentAccepted')

      case ExternAppointmentAction.Complete:
        return t('appointmentCompleted')

      case ExternAppointmentAction.Reject:
        return t('appointmentRejected')

      default:
        return ''
    }
  }

  const handleRejectSubmit = (cancelMessage: string | undefined) => {
    handleAppointmentAction(ExternAppointmentAction.Reject, cancelMessage)
  }

  const handleCloseAcceptCompleteModal = () => {
    setOpenAcceptCompleteModal(false)
  }

  const handleCompleteAppointmentAction = () => {
    handleAppointmentAction(ExternAppointmentAction.Complete)
    setOpenAcceptCompleteModal(false)
  }

  const handleAcceptAppointmentAction = () => {
    handleAppointmentAction(ExternAppointmentAction.Accept)
    setOpenAcceptCompleteModal(false)
  }

  const handleAppointmentAction = (action: ExternAppointmentAction, rejectionMessage?: string) => {
    if (!appointment?.id) return enqueueSnackbar(t('anErrorOccured'), { variant: 'error' })

    externAppointmentService
      .modifyAppointmentStatus(
        appointment.id,
        action,
        rejectionMessage,
        appointment.professional.id
      )
      .subscribe({
        next: (res) => {
          if (!res) return enqueueSnackbar(t('anErrorOccured'), { variant: 'error' })

          enqueueSnackbar(getSuccessText(action), { variant: 'success' })
          externAppointmentService.getByID(appointment.id || '').subscribe(setAppointment)
          if (action === ExternAppointmentAction.Reject) setRejectModalOpen(false)
        },
        error: () => enqueueSnackbar(t('anErrorOccured'), { variant: 'error' }),
      })
  }

  if (!appointment) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t('noAppointment')} (ID: {appointmentId})
        </Typography>
      </Box>
    )
  }

  const startDate = new Date(appointment.date)

  return (
    <Box className={`${genericStyle.pageContainer} ${styles.paperContainer}`}>
      <Paper elevation={2} className={styles.paper}>
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('appointmentDetail')}</Typography>

            <Chip
              label={externAppointmentStatusTypes()[appointment.status]}
              className={` ${styles.baseChip} ${getChipClass(appointment.status)}`}
            />
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('date')}:</strong> {dayjs(startDate).toDate().toLocaleDateString()}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>{t('schedule')}: </strong>{' '}
              <>{`${dayjs(appointment.dateFrom).format('HH:00')}-${dayjs(appointment.dateTo).format(
                'HH:00'
              )}`}</>
            </Typography>
          </Box>

          {appointment.initialMessage && (
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>
                {t('comment')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography>{appointment.initialMessage}</Typography>
              </Paper>
            </Box>
          )}
          {isFinalized && (
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>
                {`${t('rating')}`}
              </Typography>
              <Rating precision={1} readOnly value={appointment.rate} />
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography>{appointment.rateMessage}</Typography>
              </Paper>
            </Box>
          )}
          {isPending && (
            <Box mt={2} className={styles.actionsContainer}>
              <Button
                className={`${styles.actionButton} ${styles.acceptButton}`}
                type={'button'}
                onClick={() => setOpenAcceptCompleteModal(true)}
              >
                {t('accept')}
              </Button>
              <Button
                className={`${styles.actionButton} ${styles.rejectButton}`}
                type={'button'}
                onClick={() => setRejectModalOpen(true)}
              >
                {t('reject')}
              </Button>
            </Box>
          )}
          {isAccepted && (
            <Box mt={2} className={styles.actionsContainer}>
              <Button
                className={`${styles.actionButton} ${styles.completeButton}`}
                type={'button'}
                onClick={() => setOpenAcceptCompleteModal(true)}
              >
                {t('complete')}
              </Button>
              <Button
                className={`${styles.actionButton} ${styles.rejectButton}`}
                type={'button'}
                onClick={() => setRejectModalOpen(true)}
              >
                {t('reject')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
      <Modal
        open={rejectModalOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className={styles.modal}>
          <RejectAppointmentModal
            handleClose={handleClose}
            handleRejectSubmit={handleRejectSubmit}
          />
        </div>
      </Modal>
      <Modal open={openAcceptCompleteModal} onClose={handleCloseAcceptCompleteModal}>
        {appointment.status === ExternAppointmentStatus.Pending ? (
          <AcceptCompleteAppointmentModal
            title={t('acceptAppointment')}
            warningText={t('acceptAppointmentConfirm')}
            handleClose={handleCloseAcceptCompleteModal}
            handleSave={handleAcceptAppointmentAction}
          />
        ) : (
          <AcceptCompleteAppointmentModal
            title={t('completeAppointment')}
            warningText={t('completeAppointmentConfirm')}
            handleClose={handleCloseAcceptCompleteModal}
            handleSave={handleCompleteAppointmentAction}
          />
        )}
      </Modal>
    </Box>
  )
}
